import { useEffect, useRef, useCallback } from 'react';
import { useConnectionStore } from '../stores/connection.store';
import { useAuthStore } from '@/features/auth/model/auth.store';
import { useUser } from '@/features/auth/model/auth.selectors';
import { authEvents } from '@/shared/lib/auth-events';
import { wsLogger } from '@/shared/lib/logger';
import { jobQueueService } from '@/shared/api/services/job-queue.service';
import { useJobQueueStore } from '@/features/design/job-queue';
import { profileService } from '@/shared/api/services/profile.service';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { getAblyConnectionState, isTerminalAblyState } from '../utils';

interface UseWebSocketConnectionProps {
  isLeader: boolean;
}

interface UseWebSocketConnectionReturn {
  isConnected: boolean;
  hasInitiallyConnectedRef: React.RefObject<boolean>;
}

export function useWebSocketConnection({
  isLeader,
}: UseWebSocketConnectionProps): UseWebSocketConnectionReturn {
  const hasInitiallyConnectedRef = useRef(false);

  const user = useUser();
  const token = useAuthStore((state) => state.token);
  // A socket may only be opened once auth has SETTLED and the email is
  // confirmed. `/broadcasting/auth` answers 403 for an unverified account and
  // Ably treats an auth-provider rejection as terminal (error 80019) — it does
  // not retry. Without this gate the boot-time cached user + token opened a
  // doomed connection before `initializeAuth` had even discovered the pending
  // verification, and the whole session ran without realtime. The auth store
  // already gates its own `websocket:connect` events the same way; this effect
  // was the parallel path that bypassed it.
  const isAuthSettled = useAuthStore((state) => state.isInitialized);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );
  const mayConnect = isAuthSettled && !pendingEmailVerification;

  const connect = useConnectionStore((state) => state.connect);
  const disconnect = useConnectionStore((state) => state.disconnect);
  const isConnected = useConnectionStore((state) => state.isConnected);
  const forceReconnect = useConnectionStore((state) => state.forceReconnect);

  // Setup auth event listeners for WebSocket connection management
  useEffect(() => {
    const unsubscribeConnect = authEvents.on('websocket:connect', (event) => {
      const { userId, token: authToken } = event.payload as {
        userId: string;
        token: string;
      };
      connect(userId, authToken);
    });

    const unsubscribeDisconnect = authEvents.on('websocket:disconnect', () => {
      disconnect();
    });

    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
    };
  }, [connect, disconnect]);

  // Handle initial connection and reconnection when auth state changes
  // Only leader tab maintains actual WebSocket connection
  useEffect(() => {
    if (user?.id && token && isLeader && mayConnect) {
      if (!isConnected) {
        connect(user.id, token);
      }
    } else if (isConnected && !isLeader) {
      // Lost leadership - disconnect
      disconnect();
      hasInitiallyConnectedRef.current = false;
    } else if (isConnected && (!user?.id || !mayConnect)) {
      // Disconnect if no auth, or if the account is no longer allowed a socket
      disconnect();
      hasInitiallyConnectedRef.current = false;
    }
  }, [
    user?.id,
    token,
    isConnected,
    isLeader,
    mayConnect,
    connect,
    disconnect,
  ]);

  // Track when we first connect successfully
  useEffect(() => {
    if (isConnected && !hasInitiallyConnectedRef.current) {
      wsLogger.info('WebSocket first connection established');
      hasInitiallyConnectedRef.current = true;
    }
  }, [isConnected]);

  // Handle page visibility changes — lifecycle-aware WebSocket
  // Disconnect after grace period when hidden to save bandwidth, reconnect when visible
  useEffect(() => {
    let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const DISCONNECT_GRACE_PERIOD_MS = 30_000; // 30s grace before disconnecting

    const handleVisibilityChange = () => {
      if (document.hidden) {
        wsLogger.debug('Tab hidden — scheduling disconnect');

        // Disconnect after grace period to save bandwidth in background tabs
        disconnectTimer = setTimeout(() => {
          if (document.hidden && isLeader) {
            wsLogger.info(
              'Tab still hidden after grace period, disconnecting WebSocket'
            );
            disconnect();
          }
        }, DISCONNECT_GRACE_PERIOD_MS);
      } else {
        wsLogger.debug('Tab visible — checking connection health');

        // Cancel pending disconnect
        if (disconnectTimer) {
          clearTimeout(disconnectTimer);
          disconnectTimer = null;
        }

        if (user?.id && token && mayConnect) {
          const connectionState = useConnectionStore.getState();
          const echo = connectionState.echo;

          if (echo) {
            const ablyState = getAblyConnectionState(echo) ?? 'unknown';

            // Ably states: initialized, connecting, connected, disconnected, suspended, closing, closed, failed
            //
            // Only force a reconnect for states Ably can't transparently
            // recover from — see `isTerminalAblyState`. `disconnected` (and
            // `connecting`) are TRANSIENT: Ably auto-resumes them WITH message
            // continuity, so no events are missed. Forcing a reconnect there
            // tears down a healthy session and triggers a fresh channel
            // attach, which replays the `rewind=2m` backlog (duplicate toasts
            // / re-processed events). Let Ably resume on its own instead.
            if (isTerminalAblyState(ablyState)) {
              wsLogger.info(
                `Connection needs recovery after visibility (state: ${ablyState}), reconnecting...`
              );
              forceReconnect();
            } else {
              wsLogger.debug(
                `Connection state after tab visibility: ${ablyState} — letting Ably resume`
              );
            }
          } else if (!isConnected && isLeader) {
            wsLogger.info(
              'No active connection, reconnecting after tab became visible'
            );
            connect(user.id, token);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (disconnectTimer) clearTimeout(disconnectTimer);
    };
  }, [
    isConnected,
    isLeader,
    user?.id,
    token,
    mayConnect,
    connect,
    disconnect,
    forceReconnect,
  ]);

  return {
    isConnected,
    hasInitiallyConnectedRef,
  };
}

/**
 * Handle reconnection sync - sync token balance, render queue and user profile
 */
export function useReconnectSyncHandler(
  hasInitiallyConnectedRef: React.RefObject<boolean>
) {
  const handleReconnectSync = useCallback(async () => {
    // Only sync on actual reconnection, not initial connection
    if (!hasInitiallyConnectedRef.current) {
      return;
    }

    // Invalidate caches for entities whose WS instant events fired during the
    // disconnect window were dropped — staleTime of 5min would otherwise let
    // out-of-date lists/details sit on screen until next mount.
    queryClient.invalidateQueries({ queryKey: cacheKeys.projects.lists() });
    queryClient.invalidateQueries({ queryKey: cacheKeys.concepts.lists() });
    queryClient.invalidateQueries({ queryKey: cacheKeys.angles.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.conceptAngles.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.projectAngles.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.references.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.controls.all });
    queryClient.invalidateQueries({ queryKey: cacheKeys.estimates.all });
    // Building-scoped render gate: instant queue/pack changes dropped during the
    // disconnect window must not leave a stale Generate gate on screen.
    queryClient.invalidateQueries({ queryKey: cacheKeys.queueStatus.all });

    // Token balance is part of the /user profile payload — `profileService
    // .refreshProfile()` below seeds `cacheKeys.balance` via setQueryData
    // through `applyProfileData`. No separate balance round-trip needed.

    const syncPromises = [
      // 1. Sync job queue
      jobQueueService
        .getQueue(10)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((queueData: any) => {
          useJobQueueStore.getState().replaceJobQueue(queueData);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((err: any) => wsLogger.error('Failed to sync job queue:', err)),

      // 2. Sync server-authoritative render-slot aggregate. Fires in parallel
      // with /history so cold-start has a single round trip; after this WS
      // queue:snapshot keeps both fresh.
      jobQueueService
        .getQueueStatus()
        .then((aggregate) => {
          useJobQueueStore.getState().setQueueAggregate(aggregate);
        })
        .catch((err: unknown) =>
          wsLogger.error('Failed to sync queue aggregate:', err)
        ),

      // 3. Sync user profile (also seeds cacheKeys.balance via applyProfileData)
      profileService
        .refreshProfile()
        .then(() => wsLogger.info('User profile synced after reconnection'))
        .catch((err) => wsLogger.error('Failed to sync user profile:', err)),
    ];

    await Promise.allSettled(syncPromises);
  }, [hasInitiallyConnectedRef]);

  return handleReconnectSync;
}
